import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Button, Grid, Paper, Typography, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getProfile, markWelcomeVideoSeen } from "store/actions/user";
import { getClients } from "store/actions/clients";
import { getProgramLists as getFitnessPrograms } from "store/actions/program";
import { getNutritions } from "store/actions/nutritions";

export default function WelcomeDashboardSection() {
    const dispatch = useDispatch();
    const userProfile = useSelector((state) => state.user.userProfile);
    const clients = useSelector((state) => state.clients?.clients);
    const fitnessPrograms = useSelector((state) => state.programs?.programms);
    const nutritionPrograms = useSelector((state) => state.nutritions?.nutritions);
    const isLoadingClients = useSelector((state) => state.clients?.isLoadingClients);
    const isLoadingPrograms = useSelector((state) => state.programs?.isLoadingProgramms);
    const isLoadingNutritions = useSelector((state) => state.nutritions?.isLoadingNutritins);

    const [isVisible, setIsVisible] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);

    useEffect(() => {
        if (!userProfile || Object.keys(userProfile).length === 0) {
            dispatch(getProfile());
        }
        // Redux state defaults to [] or null for these lists, so we check data length
        // to safely re-fetch on refresh if they are completely empty.
        if (!clients || clients.length === 0) dispatch(getClients());
        if (!fitnessPrograms || fitnessPrograms.length === 0) dispatch(getFitnessPrograms());
        if (!nutritionPrograms || nutritionPrograms.length === 0) dispatch(getNutritions());

        // Allow Redux dispatches to process before evaluating visibility
        const timer = setTimeout(() => setIsInitializing(false), 150);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const hasPrograms = Boolean(fitnessPrograms && fitnessPrograms.length > 0);
    const hasClients = Boolean(clients && clients.length > 0);
    const hasNutritionPrograms = Boolean(nutritionPrograms && nutritionPrograms.length > 0);

    const allTasksDone = hasPrograms && hasClients && hasNutritionPrograms;

    useEffect(() => {
        if (userProfile && typeof userProfile === 'object' && Object.keys(userProfile).length > 0) {
            // If they explicitly dismissed it, never show it
            if (userProfile.hasSeenWelcomeVideo === true) {
                setIsVisible(false);
                return;
            }

            // Wait for initial mount and Redux fetching to start
            if (isInitializing || isLoadingClients || isLoadingPrograms || isLoadingNutritions) {
                return;
            }

            if (allTasksDone) {
                // Hide if all tasks are completed
                setIsVisible(false);
            } else {
                // Show guide
                setIsVisible(true);
            }
        }
    }, [userProfile, allTasksDone, isInitializing, isLoadingClients, isLoadingPrograms, isLoadingNutritions]);

    const handleDismiss = () => {
        setIsVisible(false);
        dispatch(markWelcomeVideoSeen());
    };

    if (!isVisible) return null;

    const onboardingSteps = [
        { label: "Ajouter mon premier client", isDone: hasClients },
        { label: "Créer mon premier programme fitness", isDone: hasPrograms },
        { label: "Créer mon premier programme nutrition", isDone: hasNutritionPrograms }
    ];

    return (
        <Paper
            elevation={3}
            sx={{
                p: { xs: 2, md: 4 },
                mt: { xs: 1, md: 2 },
                mb: 4,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                border: "1px solid rgba(0,0,0,0.05)"
            }}
        >
            <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Box sx={{ mb: { xs: 0, md: 2 } }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: "#1a237e", fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
                            Bienvenue sur Evocoach ! 🚀
                        </Typography>
                        <Typography variant="body1" sx={{ color: "text.secondary", mb: { xs: 1.5, md: 3 }, fontSize: { xs: "0.875rem", md: "1rem" } }}>
                            Découvrez comment tirer le meilleur parti de votre espace coach grâce à cette courte présentation.
                        </Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: "0.875rem", md: "1rem" } }}>
                            Vos prochaines étapes :
                        </Typography>
                        <List dense disablePadding>
                            {onboardingSteps.map((step, index) => (
                                <ListItem key={index} sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {step.isDone ? (
                                            <CheckCircleIcon color="success" />
                                        ) : (
                                            <RadioButtonUncheckedIcon sx={{ color: "text.disabled" }} />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={step.label}
                                        primaryTypographyProps={{
                                            fontWeight: 500,
                                            color: step.isDone ? "text.secondary" : "text.primary",
                                            sx: { textDecoration: step.isDone ? "line-through" : "none" }
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>

                        <Box sx={{ mt: { xs: 2, md: 3 }, mb: { xs: 1, md: 0 } }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDismiss}
                                sx={{ borderRadius: "8px", textTransform: "none", px: { xs: 3, md: 4 }, py: 1 }}
                            >
                                Masquer ce guide
                            </Button>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            position: "relative",
                            paddingTop: "56.25%",
                            width: "100%",
                            borderRadius: "12px",
                            overflow: "hidden",
                            bgcolor: "#000",
                            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
                        }}
                    >
                        <video
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                            src="https://evocoach.nyc3.cdn.digitaloceanspaces.com/assets/evocoach-intro.mp4"
                            controls
                        />
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
}
